import {Json} from '../../../components/JsonObjectCard';
import {RequestExample} from '../../../components/RequestExampleCard';
import {RequestDefinitionData} from '../../../components/RequestDefinition';
import {Attribute} from '../../../components/ObjectAttribute';

const updateWorkOrderRequest: RequestExample = {
    title: {
        verb: 'POST',
        endpointUrl: '/api/work-orders/:id',
    },
    curl:
        `curl --location \\
--request POST '<YOUR SERVER>/api/work-orders/:id' \\
--header 'Content-Type: application/json' \\
--header 'Authorization: Bearer <TOKEN>' \\
--data-raw '<WORK ORDER JSON>'
`,
};

const updateWorkOrderResponse: Json = {
    title: 'RESPONSE',
    json: {
        "id": 41,
        "number": "1024:1",
        "status": "Entered",
        "moItem": {
            "id": 385,
            "description": "Bike, 26 inch"
        },
        "location": {
            "id": 20,
            "name": "Manufacturing"
        },
        "locationGroup": {
            "id": 5,
            "name": "SLC"
        },
        "customer": {
            "id": 7,
            "name": "Sam Ash"
        },
        "class": {
            "id": 1,
            "name": "None"
        },
        "category": {
            "id": 12,
            "name": "Assembly"
        },
        "priority": "2-High",
        "quantityOrdered": 1,
        "quantityTarget": 1,
        "quantityScrapped": 0,
        "cost": "0",
        "note": "Rush this build.",
        "dateCreated": "2021-05-14T15:49:18.000-0600",
        "dateScheduled": "2021-05-21T15:49:08.160-0600",
        "dateScheduledToStart": "2021-05-14T15:49:08.160-0600",
        "dateStarted": null,
        "dateFinished": null,
        "lastModified": {
            "username": "admin",
            "dateLastModified": "2021-05-15T09:12:44.310-0600"
        },
        "assignedUsers": [
            {
                "id": 1,
                "name": "admin",
                "description": "Administrator"
            }
        ],
        "woItems": [
            {
                "id": 101,
                "part": {
                    "id": 80,
                    "name": "BK100",
                    "description": "New to Biking Kit"
                },
                "type": {
                    "id": 10,
                    "name": "Finished Good"
                },
                "uom": {
                    "id": 1,
                    "name": "Each",
                    "abbreviation": "ea"
                },
                "quantity": "1",
                "quantityUsed": "0",
                "quantityScrapped": "0",
                "cost": "0",
                "description": "New to Biking Kit",
                "destLocation": {
                    "id": 23,
                    "name": "Finished Goods"
                },
                "consumeItems": []
            }
        ],
        "steps": [
            {
                "id": 7,
                "name": "Assemble frame",
                "description": "Attach the wheels to the frame.",
                "url": "",
                "details": "",
                "sortOrder": 1,
                "quantityReady": 0,
                "quantityFulfilled": 0,
                "dateFirstReady": null,
                "dateLastFulfilled": null
            }
        ]
    },
};

const updateWorkOrderAttributes: Attribute[] = [
    {
        name: 'note',
        type: 'string',
        optional: true,
        description: 'The work order\'s note field.',
    },
    {
        name: 'priority',
        type: 'string',
        optional: true,
        description: 'The name of the priority level, for example \'3-Normal\'.',
    },
    {
        name: 'location',
        type: 'reference object',
        optional: true,
        description: 'The location the work order is staged in. The work order is also moved to that location\'s location group.',
        children: [
            {
                name: 'id',
                type: 'integer',
                description: 'The unique identification number.',
            },
        ]
    },
    {
        name: 'locationGroup',
        type: 'reference object',
        optional: true,
        description: 'The location group the work order belongs to. Sent on its own it moves the work order to that group\'s default manufacturing location; send a location instead to choose the location yourself. Ignored when a location is also sent.',
        children: [
            {
                name: 'id',
                type: 'integer',
                description: 'The unique identification number.',
            },
        ]
    },
    {
        name: 'dateScheduledToStart',
        type: 'date',
        optional: true,
        description: 'Timestamp of when the work order is scheduled to start. Must not be after dateScheduled.',
    },
    {
        name: 'dateScheduled',
        type: 'date',
        optional: true,
        description: 'Timestamp of when the work order is scheduled to be finished.',
    },
    {
        name: 'customer',
        type: 'reference object',
        optional: true,
        description: 'The customer/job the work order is for. Send an id of 0 to clear it.',
        children: [
            {
                name: 'id',
                type: 'integer',
                description: 'The unique identification number.',
            },
        ]
    },
    {
        name: 'class',
        type: 'reference object',
        optional: true,
        description: 'The class category. Only editable while class tracking is turned on. Send an id of 0 to clear it.',
        children: [
            {
                name: 'id',
                type: 'integer',
                description: 'The unique identification number.',
            },
        ]
    },
    {
        name: 'category',
        type: 'reference object',
        optional: true,
        description: 'The calendar category. Send an id of 0 to clear it.',
        children: [
            {
                name: 'id',
                type: 'integer',
                description: 'The unique identification number.',
            },
        ]
    },
    {
        name: 'assignedUsers',
        type: 'list',
        optional: true,
        description: 'The users assigned to the work order. The list replaces the current assignments, so users left out are unassigned. Requires the Assign Users right.',
        children: [
            {
                name: 'id',
                type: 'integer',
                description: 'The user\'s unique identification number.',
            },
        ]
    },
    {
        name: 'steps',
        type: 'list',
        optional: true,
        description: 'The work instruction steps. The list replaces the current steps, so steps left out are deleted, steps sent with an id are updated and steps sent without one are added. Requires the Edit Work Order Instructions right.',
        children: [
            {
                name: 'id',
                type: 'integer',
                optional: true,
                description: 'The id of an existing step. Leave it out to add a new step.',
            },
            {
                name: 'name',
                type: 'string',
                description: 'The name of the step.',
            },
            {
                name: 'description',
                type: 'string',
                optional: true,
                description: 'The description of the step.',
            },
            {
                name: 'url',
                type: 'string',
                optional: true,
                description: 'The url link on the step.',
            },
            {
                name: 'details',
                type: 'string',
                optional: true,
                description: 'The details of the step.',
            },
            {
                name: 'sortOrder',
                type: 'integer',
                optional: true,
                description: 'The position of the step in the list.',
            },
        ]
    },
    {
        name: 'lastModified',
        type: 'object',
        optional: true,
        description: 'Send the dateLastModified returned by the get endpoint to be told when someone else has saved the work order in the meantime. The request is rejected with a 409 when it no longer matches.',
        children: [
            {
                name: 'dateLastModified',
                type: 'date',
                description: 'Timestamp of the last time the work order was modified.',
            },
        ]
    },
];

const data: RequestDefinitionData = {
    id: 'update-work-order',
    title: 'Update a work order',
    description: 'Saves edits to an open work order and returns the updated work order. Only the attributes below are ' +
        'editable: the status, quantities, costs, work order items and step fulfillment data are read only, and no ' +
        'inventory is moved. Attributes left out of the request keep their stored value. A fulfilled work order can no ' +
        'longer be changed, and the status cannot be changed from this endpoint.',
    endpoint: {
        verb: 'POST',
        path: '/work-orders/:id',
        tooltip: 'Update a work order',
    },
    attributes: updateWorkOrderAttributes,
    requestObject: updateWorkOrderRequest,
    responseObject: updateWorkOrderResponse,
};

export default data;
